import { useMemo } from 'react'
import { KanbanBoard, type KanbanItem, type KanbanStage } from '@/components/ui'
import type { CaseSummary, Stage } from '../api/types'
import {
  BOARD_STAGES,
  CATEGORY_LABELS,
  CLAIM_STATUS_LABELS,
  STAGE_SHORT_LABELS,
  STAGE_VARIANT,
  filedWithLabel,
  transitionAllowed,
} from '../utils/stage-meta'

interface CaseKanbanItem extends KanbanItem {
  case: CaseSummary
}

export interface MaterialDispositionKanbanProps {
  items: CaseSummary[]
  selectedId?: string
  onSelect: (c: CaseSummary) => void
  onTransition: (c: CaseSummary, target: Stage) => void
}

function kanbanAccent(
  v: 'neutral' | 'success' | 'warning' | 'danger' | 'info',
): 'neutral' | 'success' | 'warning' | 'danger' {
  return v === 'info' ? 'neutral' : v
}

const STAGES: KanbanStage[] = BOARD_STAGES.map((s) => ({
  id: s,
  label: STAGE_SHORT_LABELS[s],
  accent: kanbanAccent(STAGE_VARIANT[s]),
}))

export function MaterialDispositionKanban({
  items, selectedId, onSelect, onTransition,
}: MaterialDispositionKanbanProps) {
  const kanbanItems: CaseKanbanItem[] = useMemo(
    () => items.map((c) => ({ id: c.id, stageId: c.stage, case: c })),
    [items],
  )

  return (
    <KanbanBoard<CaseKanbanItem>
      stages={STAGES}
      items={kanbanItems}
      selectedId={selectedId}
      allowedTransitions={(from, to) => transitionAllowed(from as Stage, to as Stage)}
      onAdvance={(item, _from, to) => onTransition(item.case, to as Stage)}
      onSelect={(item) => onSelect(item.case)}
      renderCard={(item) => <CaseKanbanCard c={item.case} />}
      scrollRestorationKey="material-disposition"
    />
  )
}

function formatMoney(value: string | null): string | null {
  if (!value) return null
  const n = Number(value)
  if (Number.isNaN(n)) return null
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

function productSummary(c: CaseSummary): string | null {
  if (!c.first_product_name) return null
  return c.line_count > 1 ? `${c.first_product_name} +${c.line_count - 1} more` : c.first_product_name
}

function CaseKanbanCard({ c }: { c: CaseSummary }) {
  const value = formatMoney(c.estimated_value)
  const product = productSummary(c)
  const filedWith = filedWithLabel(c)
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-role-data-primary truncate" title={product ?? ''}>
        {product ?? <em className="text-tertiary">unknown product</em>}
      </div>
      <div className="text-role-data-secondary truncate">{c.case_number}</div>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-tertiary">{CATEGORY_LABELS[c.category]}</span>
        {value ? <span className="text-tertiary">· {value}</span> : null}
      </div>
      {c.vendor_name ? (
        <div className="line-clamp-1 text-role-metadata" title={c.vendor_name}>
          {c.vendor_name}
        </div>
      ) : null}
      {filedWith ? (
        <div className="line-clamp-1 text-role-metadata" title={filedWith}>
          Filed with: {filedWith}
          {c.claim_status ? ` · ${CLAIM_STATUS_LABELS[c.claim_status]}` : ''}
        </div>
      ) : null}
    </div>
  )
}
